import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import store from '@/store';
import { authActions } from '@/store/reducers/auth';
import { gameActions } from '@/store/reducers/game';
let forcedLogoutInProgress = false;

function getAuthorizationHeader(
  config: InternalAxiosRequestConfig | undefined
): string | undefined {
  if (!config?.headers) return undefined;
  const h = config.headers as Record<string, unknown> & {
    get?: (name: string) => string | undefined;
  };
  if (typeof h.get === 'function') {
    return h.get('Authorization') ?? h.get('authorization');
  }
  return (h.Authorization ?? h.authorization) as string | undefined;
}

function flattenResponseMessage(data: unknown, depth = 0): { text: string; typeName?: string } {
  if (depth > 5) return { text: '' };
  if (data == null) return { text: '' };
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      if (parsed && typeof parsed === 'object') {
        return flattenResponseMessage(parsed, depth + 1);
      }
    } catch {
      return { text: data };
    }
    return { text: data };
  }
  if (typeof data !== 'object') return { text: String(data) };
  const o = data as Record<string, unknown>;
  if (o.data && typeof o.data === 'object') {
    return flattenResponseMessage(o.data, depth + 1);
  }
  const parts: string[] = [];
  if (typeof o.message === 'string') parts.push(o.message);
  if (typeof o.__type === 'string') parts.push(o.__type);
  if (typeof o.name === 'string') parts.push(o.name);
  if (typeof o.code === 'string') parts.push(o.code);
  if (typeof o.error === 'string') parts.push(o.error);
  return { text: parts.join(' '), typeName: typeof o.__type === 'string' ? o.__type : undefined };
}

function isDealExpiredMessage(text: string): boolean {
  const t = text.toLowerCase();
  return t.includes('this deal has expired') || t.includes('user deal has expired');
}

function isLikelyWrongPasswordMessage(combined: string): boolean {
  return (
    combined.includes('incorrect username or password') ||
    combined.includes('incorrect username') ||
    combined.includes('incorrect password')
  );
}

/**
 * Matches Cognito / API bodies such as:
 * { __type: "NotAuthorizedException", message: "Access Token has been revoked" }
 * Note: Cognito uses "Access Token has been revoked" (with "has been") — not the substring "access token revoked".
 */
function isCognitoSessionInvalidCombinedMessage(combined: string): boolean {
  if (isLikelyWrongPasswordMessage(combined)) return false;
  if (isDealExpiredMessage(combined)) return false;

  if (combined.includes('has been revoked')) return true;
  if (combined.includes('access token') && combined.includes('revoked')) return true;
  if (combined.includes('refresh token') && combined.includes('revoked')) return true;
  if (combined.includes('token has expired') || combined.includes('token expired')) return true;
  if (combined.includes('invalid or expired')) return true;
  if (combined.includes('invalid token')) return true;
  return false;
}

/**
 * Errors from amazon-cognito-identity-js / direct cognito-idp responses (not Axios).
 * Shape: { code: "NotAuthorizedException", message: "Access Token has been revoked" } or __type instead of code.
 */
export function isCognitoSdkSessionInvalidError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;
  const message = typeof e.message === 'string' ? e.message.toLowerCase() : '';
  const code = typeof e.code === 'string' ? e.code : '';
  const name = typeof e.name === 'string' ? e.name : '';
  const typ = typeof e.__type === 'string' ? e.__type : '';

  const isNotAuthorized =
    code === 'NotAuthorizedException' ||
    name === 'NotAuthorizedException' ||
    typ === 'NotAuthorizedException';

  if (isNotAuthorized) {
    if (isLikelyWrongPasswordMessage(message)) return false;
    if (message.includes('password attempts exceeded')) return false;
    return (
      message.includes('revoked') ||
      message.includes('expired') ||
      (message.includes('token') && message.includes('invalid'))
    );
  }

  return isCognitoSessionInvalidCombinedMessage(`${message} ${typ} ${code}`);
}

/**
 * Returns true when the API error indicates the Cognito / session token is no longer valid
 * (revoked, expired, or unauthorized for the protected resource).
 */
export function isForcedSessionAuthError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  const ae = error as AxiosError;
  const status = ae.response?.status;
  if (!status) return false;

  const { text, typeName } = flattenResponseMessage(ae.response?.data);
  const combined = `${text} ${typeName ?? ''}`.toLowerCase();
  const hadBearer = !!getAuthorizationHeader(ae.config) || !!localStorage.getItem('token');

  if (isCognitoSessionInvalidCombinedMessage(combined)) {
    return true;
  }

  if (status === 401) {
    if (isDealExpiredMessage(combined)) return false;
    return hadBearer;
  }

  if (status === 403) {
    const msg = (ae.response?.data as { message?: string } | undefined)?.message;
    if (msg === 'User registration limit reached. No new users can be registered at this time.') {
      return false;
    }
    if (hadBearer && (combined.includes('revoked') || combined.includes('unauthorized'))) {
      return true;
    }
  }

  return false;
}

export function unwrapThunkAxiosError(error: unknown): unknown {
  if (error && typeof error === 'object' && 'error' in error) {
    return (error as { error: unknown }).error;
  }
  return error;
}

function clearAuthCookies(): void {
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const eq = part.indexOf('=');
    const name = (eq >= 0 ? part.slice(0, eq) : part).trim();
    if (!name) continue;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    const host = window.location.hostname;
    if (host.includes('.')) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${host}`;
    }
  }
}

/**
 * Clears persisted auth, in-memory caches, and navigates to the deal-error-message page (session ended).
 * Uses location.replace so the protected shell is torn down immediately without a manual refresh.
 */
export function triggerForcedSessionLogout(replaceUrl: string = '/deal-error-message'): void {
  if (forcedLogoutInProgress) return;
  forcedLogoutInProgress = true;

  // Read the role before signOut() wipes it, so deal-error-message can show role-specific copy.
  const role = store.getState().auth.userRole;

  try {
    store.dispatch(gameActions.clearAll());
    store.dispatch(authActions.signOut());
  } catch {
    // ignore
  }

  try {
    sessionStorage.clear();
  } catch {
    // ignore
  }

  // Let the existing deal-error-message page show the correct "already expired" copy.
  // We store these flags after clearing so they survive the hard redirect.
  try {
    sessionStorage.setItem('dealErrorMessageReason', 'dealExpired');
    sessionStorage.setItem('dealErrorMessageRole', role ?? '');
  } catch {
    // ignore
  }

  try {
    clearAuthCookies();
  } catch {
    // ignore
  }

  window.location.replace(new URL(replaceUrl, window.location.origin).toString());
}
